import React, {useState, useEffect, useMemo, Fragment} from 'react';
import styled from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useActiveFrame, useEngagementId} from '../../../../../../util/customHooks';
import {useAresUrlCompareResponsesParameters, useIsAresView} from '../../../../../../util/Ares/customHooks';
import {getTaskById as getTaskByIdApi} from '../../../../../../actions/Ares/TaskList/tasklistactions';
import {serviceURL, documentsApiExpandableItems} from '../../../../../../util/uiconstants';
import Button from '../../../../../Common/BaseComponentWrappers/MotifButton';
import HeaderList from '../../../../../Common/HeaderSectionList/Header/HeaderList';
import {getTaskDocumentByDocumentId} from '../../../../../../selectors/Ares/selectors';
import LoadingIndicator from '../../../../../Common/LoadingIndicator/LoadingIndicator';
import {labels, objectComparator} from '../../../../../../util/utils';
import env from '../../../../../../util/env';
import {hardwareIcKeyboardBackspace24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {getDocumentByDocumentId} from '../../../../../../actions/Ares/taskDocumentActions';
import {getAllDocuments} from '../../../../../../actions/Document/documentactions';
import {loadStorage} from '../../../../../Common/localStorage';
import CompareDocumentDropdown from './CompareDocumentDropdown';
import {frameEvents, toggleFrameModal} from '@ey/canvascoreservices';
import EllipsesControl from '../../../../../../components/Common/EllipsesControl/EllipsesControl';
import ReactiveTabRenamer from '../../../../../../components/Common/Modal/ReactiveTabRenamer';

export default function CompareResponsesV2Container() {
	const {taskId, documentId} = useAresUrlCompareResponsesParameters();

	const compareResponsesBackUrl = loadStorage('compareResponsesBackUrl');

	const navigate = useNavigate();

	const dispatch = useDispatch();

	const engagementId = useEngagementId();

	const isAresView = useIsAresView();

	const formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));

	const documents = useSelector((state) => state.documents);

	const [documentOptions, setDocumentOptions] = useState([]);

	const [isLoading, setIsLoading] = useState(false);

	const {toggled} = useActiveFrame();

	useEffect(() => {
		setIsLoading(true);

		const getDocumentByIdPromise = dispatch(
			getDocumentByDocumentId(
				engagementId,
				documentId,
				{
					expand: documentsApiExpandableItems.tasks,
					includeApprovals: true
				},
				isAresView
			)
		).then((responseDoc) => {
			if (responseDoc && responseDoc.knowledgeFormId) {
				const baseUrl = env.getBaseURL(serviceURL, engagementId) + 'Documents';
				const guidedWorkFlowUrl = `?filterKnowledgeForm=${responseDoc.knowledgeFormId}`;
				return dispatch(getAllDocuments(baseUrl + guidedWorkFlowUrl));
			}
		});

		const getTaskByIdPromise = dispatch(getTaskByIdApi(engagementId, taskId || formData?.taskId));

		Promise.all([getDocumentByIdPromise, getTaskByIdPromise]).finally(() => {
			setIsLoading(false);
		});
	}, []);

	const onBackClickHandler = () => {
		if (toggled) {
			toggleFrameModal({frameArgs: {event: frameEvents.TOGGLE_DRAWER}});
		}
		compareResponsesBackUrl && navigate(compareResponsesBackUrl);
	};

	useEffect(() => {
		const options = [];

		if (documents && documents.allIds && documents.allIds.length) {
			options.push({
				label: labels.selectAll,
				value: 'select_all',
				level: 0,
				isChecked: true
			});

			documents.allIds.forEach((id) => {
				options.push({
					value: id,
					label: documents.byId[id].documentName,
					level: 1,
					isChecked: true,
					disabled: id === documentId
				});
			});

			options.sort((a, b) => {
				a.label === labels.selectAll ? -1 : a.label.localeCompare(b.label);
			});
		}

		setDocumentOptions(options);
	}, [documentId, documents]);

	const selectedDocumentIds = useMemo(() => {
		return documentOptions.filter((item) => item.isChecked && item.level !== 0).map((item) => item.value);
	}, [documentOptions]);

	const docName = useMemo(() => {
		if (documents && documents?.byId[documentId]?.documentName) {
			return documents?.byId[documentId]?.documentName;
		}

		return '';
	}, [documents, documentId]);


	const headerList = useMemo(() => {
		if (formData) {
			return (
				<HeaderList
					headers={formData?.headers || []}
					documentTypeId={formData.documentTypeId}
					taskId={taskId || formData.taskId}
					source="CompareResponses"
					documentIds={selectedDocumentIds}
				/>
			);
		}

		return null;
	}, [formData, taskId, selectedDocumentIds]);

	return (
		<ReactiveTabRenamer>
			<StyledCompareResponsesV2Container className="mainResponsiveWrapper StyledCompareResponsesV2Container">
				{compareResponsesBackUrl && (
					<Button
						variant="text"
						className="back-link"
						onClick={onBackClickHandler}
						showIcon
						iconSource={hardwareIcKeyboardBackspace24px}
						showLabel
						buttonLabel={labels.back}
					/>
				)}
				{isLoading ? (
					<LoadingIndicator view="blockview" show />
				) : (
					<Fragment>
						<section className="compare-responses-title-section">
							<h1 className="compare-responses-title-heading">
								<span className="compareLabel">{labels.compareResponsesLabel}:</span>
								<span className="docNameDisplay">
									<EllipsesControl content={docName} tooltip={docName} noOfLines={1} />
								</span>
							</h1>
						</section>
						<CompareDocumentDropdown
							className="compare-document-dropdown-component"
							options={documentOptions}
							setOptions={setDocumentOptions}
							selectedCount={selectedDocumentIds.length}
						/>
						<section className="compareResponsesBody">
							<section className={`snapshotTrackChangesBody`}>{headerList}</section>
						</section>
					</Fragment>
				)}
			</StyledCompareResponsesV2Container>
		</ReactiveTabRenamer>
	);
}

const StyledCompareResponsesV2Container = styled.section`
	&.StyledCompareResponsesV2Container {
		width: 100%;
		padding: 0 var(--px-116) 0 var(--px-126) !important;
		.back-link {
			margin-top: var(--px-33);
			span {
				color: var(--blue-600);
				font-weight: 700;
				line-height: var(--px-22);
				font-size: var(--px-14);
				svg {
					fill: var(--blue-600);
				}
			}
			&:hover,
			&:focus {
				box-shadow: none;
			}
		}
		.compare-responses-title-section {
			.compare-responses-title-heading {
				font-size: var(--px-32);
				color: var(--neutrals-1000);
				font-weight: 400;
				line-height: var(--px-42);
				margin: var(--px-46) 0 var(--px-23) 0;
				display: flex;
				.docNameDisplay {
					display: inline-block;
					width: auto;
					overflow: hidden;
					flex: 1;
					margin-left: var(--px-10);
				}
			}
		}
		.compareResponsesBody {
			padding-top: var(--px-40);
			margin-top: var(--px-40);
			border-top: var(--px-1) solid var(--neutrals-300);
			.snapshotTrackChangesBody {
				.StyledHeaderList {
					padding: 0;
				}
			}
		}
		.compareResponseDefaultMessage {
			display: flex;
			font-size: var(--px-16);
			color: var(--neutrals-700);
			font-weight: 400;
			line-height: var(--px-22);
			padding-top: var(--px-38);
			border-top: var(--px-1) solid var(--neutrals-300);
		}
	}
`;
